import { useCallback, useRef, useState } from 'react'

/**
 * Drag-and-drop for a file upload, in one place.
 *
 * Every upload in Wingspace takes a drop, not just a click (Alex, 2026-08-19),
 * and the reasons they'd each get subtly wrong are the reasons this is shared:
 *
 * - **`dragover` must be prevented** or the browser never fires `drop` at all —
 *   the single easiest thing to leave out, and it fails silently.
 * - **`dragenter`/`dragleave` fire per child element**, so a target with
 *   anything inside it flickers unless enter and leave are counted rather than
 *   toggled.
 * - **Not every drag is a file.** The credits list and the photo grid are both
 *   drag-to-reorder, so a target that lit up for any drag at all would fight
 *   the reordering happening on top of it.
 *
 * Where there's a real `<input type="file">` the files are written into it
 * rather than kept in React state: the form these sit in posts as multipart,
 * and only what the input holds is submitted. `DataTransfer` is the one way to
 * set `input.files` by hand — the same trick ProfileSection's cropper uses.
 *
 * **A `multiple` input accumulates across drops.** Assigning `input.files`
 * replaces the whole list, so dropping four Notion zips one at a time used to
 * leave you holding the fourth — and dropping them one at a time is exactly
 * what the import page asks for, since Notion exports one zip per database.
 * Dropping onto a target that already holds files is someone adding to the
 * pile. A single-file input still replaces, which is what dropping a second
 * poster means.
 */
interface FileDropOptions {
  /**
   * The input the dropped files should land in. Given one, the hook assigns
   * them and fires `change`, so whatever the input already did on picking
   * happens identically on dropping.
   */
  inputRef?: React.RefObject<HTMLInputElement | null>
  /** Called with what was dropped, after filtering. For uploads that don't go through a form. */
  onFiles?: (files: File[]) => void
  /** Ignore drops entirely — mid-upload, or on a disabled control. */
  disabled?: boolean
}

interface FileDropResult {
  /** True while a file is over the target. Style the drop zone from this. */
  dragging: boolean
  /**
   * True while a dropped folder is being walked. Reading a photographer's
   * folder of 400 files is not instant, and without this the drop zone goes
   * back to "drop your files here" and sits there looking like it ignored you.
   */
  reading: boolean
  /** Spread onto the element that should accept the drop. */
  dropProps: {
    onDragEnter: (event: React.DragEvent) => void
    onDragOver: (event: React.DragEvent) => void
    onDragLeave: (event: React.DragEvent) => void
    onDrop: (event: React.DragEvent) => void
  }
}

/** A drag carrying files, as against one carrying a row being reordered. */
function carriesFiles(event: React.DragEvent) {
  return Array.from(event.dataTransfer.types).includes('Files')
}

/**
 * Where a file came from inside a dropped folder — "Hamlet 2016/press/03.jpg".
 *
 * Kept beside the File rather than on it, because a File's properties are
 * read-only and the standard `webkitRelativePath` is only populated by a
 * directory *picker*, never by a drop. Callers that don't care never look.
 */
const paths = new WeakMap<File, string>()

export function droppedPath(file: File): string | null {
  return paths.get(file) ?? null
}

// A dropped folder is walked, not trusted. These are what stop a symlink loop
// or somebody's entire home directory from hanging the tab.
const MAX_DROPPED_FILES = 2_000
const MAX_DEPTH = 6

type Entry = {
  isFile: boolean
  isDirectory: boolean
  fullPath: string
  file: (onSuccess: (file: File) => void, onError: (error: unknown) => void) => void
  createReader: () => { readEntries: (onSuccess: (entries: Entry[]) => void, onError: (error: unknown) => void) => void }
}

function fileFrom(entry: Entry): Promise<File | null> {
  return new Promise((resolve) => entry.file(resolve, () => resolve(null)))
}

/**
 * `readEntries` returns at most 100 at a time and signals the end with an empty
 * batch — a detail that silently truncates any folder of more than 100 photos
 * if you call it once and believe the answer.
 */
function readAll(entry: Entry): Promise<Entry[]> {
  const reader = entry.createReader()
  const found: Entry[] = []

  return new Promise((resolve) => {
    const readBatch = () => {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(found)
          return
        }
        found.push(...batch)
        readBatch()
      }, () => resolve(found))
    }
    readBatch()
  })
}

async function walk(entries: Entry[], depth: number, collected: File[]) {
  for (const entry of entries) {
    if (collected.length >= MAX_DROPPED_FILES) return

    if (entry.isFile) {
      const file = await fileFrom(entry)
      if (!file) continue
      // Leading slash off: fullPath is "/Hamlet 2016/03.jpg".
      paths.set(file, entry.fullPath.replace(/^\//, ''))
      collected.push(file)
    } else if (entry.isDirectory && depth < MAX_DEPTH) {
      await walk(await readAll(entry), depth + 1, collected)
    }
  }
}

/**
 * What was actually dropped, folders expanded.
 *
 * `dataTransfer.files` never expands a directory — drop a folder and the
 * browser hands over one entry that *is* the folder, which then fails the
 * `accept` filter and disappears without a word. `webkitGetAsEntry()` is the
 * only way through, and the entry list has to be taken *synchronously*: the
 * DataTransfer is emptied the moment the handler returns, so a single `await`
 * before this line loses the drop entirely.
 */
function entriesIn(event: React.DragEvent): Entry[] {
  return Array.from(event.dataTransfer.items)
    .map((item) => (item.webkitGetAsEntry?.() as Entry | null) ?? null)
    .filter((entry): entry is Entry => entry !== null)
}

/**
 * Only what the input would have accepted from the picker. The server checks
 * this again — it has to, since nothing here reaches it — but refusing a
 * mistake before it's uploaded is the difference between an error message and
 * an error message that arrives forty seconds later.
 *
 * An input with no `accept` takes everything, which is what a blank list means.
 */
function matchesAccept(file: File, accept: string) {
  const rules = accept
    .split(',')
    .map((rule) => rule.trim().toLowerCase())
    .filter(Boolean)
  if (rules.length === 0) return true

  const name = file.name.toLowerCase()
  const type = file.type.toLowerCase()

  return rules.some((rule) => {
    if (rule.startsWith('.')) return name.endsWith(rule)
    if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1))
    return type === rule
  })
}

/**
 * Enough to tell two files apart for the purpose of "have I already got this
 * one". A File carries no identity beyond its metadata, and name-and-size alone
 * would collapse two different photos exported at the same dimensions.
 */
function fileKey(file: File) {
  return `${file.name}:${file.size}:${file.lastModified}`
}

export function useFileDrop({ inputRef, onFiles, disabled = false }: FileDropOptions): FileDropResult {
  const [dragging, setDragging] = useState(false)
  const [reading, setReading] = useState(false)
  // Counted, not toggled: moving the pointer between a drop zone and the text
  // inside it fires leave-then-enter, and a boolean would flicker off on every
  // one of them.
  const depth = useRef(0)

  const reset = useCallback(() => {
    depth.current = 0
    setDragging(false)
  }, [])

  const onDragEnter = useCallback(
    (event: React.DragEvent) => {
      if (disabled || !carriesFiles(event)) return
      event.preventDefault()
      depth.current += 1
      setDragging(true)
    },
    [disabled],
  )

  const onDragOver = useCallback(
    (event: React.DragEvent) => {
      if (disabled || !carriesFiles(event)) return
      // Without this the browser treats the element as not a drop target and
      // no `drop` event is ever delivered.
      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
    },
    [disabled],
  )

  const onDragLeave = useCallback(
    (event: React.DragEvent) => {
      if (disabled || !carriesFiles(event)) return
      event.preventDefault()
      depth.current -= 1
      if (depth.current <= 0) reset()
    },
    [disabled, reset],
  )

  // Everything that happens to a dropped file once we actually have one —
  // shared by the plain path and the folder-walk path, which arrives here an
  // await or two later holding the same thing.
  const accept = useCallback(
    (dropped: File[]) => {
      const input = inputRef?.current
      const rules = input?.accept ?? ''
      const multiple = input ? input.multiple : true

      let files = dropped.filter((file) => matchesAccept(file, rules))
      if (files.length === 0) return
      if (!multiple) files = files.slice(0, 1)

      // What the input already holds, kept only where it can hold more than
      // one. Dropping the same file twice is one file: the second drop is
      // someone making sure rather than asking for a duplicate upload.
      const kept = input && multiple ? Array.from(input.files ?? []) : []
      const seen = new Set(kept.map(fileKey))
      const added = files.filter((file) => !seen.has(fileKey(file)))
      if (added.length === 0) return

      if (input) {
        const transfer = new DataTransfer()
        kept.forEach((file) => transfer.items.add(file))
        added.forEach((file) => transfer.items.add(file))
        input.files = transfer.files
        // So anything already listening to the picker — a filename list, an
        // auto-submit — runs for a drop without knowing the difference.
        input.dispatchEvent(new Event('change', { bubbles: true }))
      }

      // Only what this drop added. The ones already there have been handled —
      // on the photo grid they're uploaded the moment they land, and handing
      // them back would upload every earlier drop a second time.
      onFiles?.(added)
    },
    [inputRef, onFiles],
  )

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      if (disabled || !carriesFiles(event)) return
      event.preventDefault()
      reset()

      // Both read now, before any await: the DataTransfer is emptied the moment
      // this handler returns, so anything taken after the first suspension
      // point comes back empty.
      const dropped = Array.from(event.dataTransfer.files)
      const entries = entriesIn(event)

      // The overwhelmingly common case, and the one that must stay synchronous
      // — a folder walk is only paid for by someone who dropped a folder.
      if (!entries.some((entry) => entry.isDirectory)) {
        accept(dropped)
        return
      }

      setReading(true)
      const collected: File[] = []
      walk(entries, 0, collected).finally(() => {
        setReading(false)
        accept(collected)
      })
    },
    [accept, disabled, reset],
  )

  return { dragging, reading, dropProps: { onDragEnter, onDragOver, onDragLeave, onDrop } }
}
